<template>
  <div>
    <AlertLayout
      title="Reminders!"
      message="Kindly observe the facility's turn around time policy on received blood requisitions.<br>Click on Details button for more actions and information on the requisition of choice."
      type="warning"
    />
    <div class="flex flex-wrap items-center justify-between py-1 pb-4">
      <div class="flex items-center space-x-4">
        <div class="relative" v-if="currentTab === 'donations' || currentTab === 'reactions'">
          <button
            id="dropdownRadioButton"
            class="inline-flex items-center px-6 py-2 text-gray-400 bg-white border border-gray-300 rounded-md"
            type="button"
            @click="toggleDropdown"
            :disabled="isLoadingBloodGroups"
          >
            <svg
              class="w-6 h-6"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              fill="none"
              viewBox="0 0 24 24"
            >
              <path
                stroke="currentColor"
                stroke-linecap="round"
                stroke-width="2"
                d="M18.796 4H5.204a1 1 0 0 0-.753 1.659l5.302 6.058a1 1 0 0 1 .247.659v4.874a.5.5 0 0 0 .2.4l3 2.25a.5.5 0 0 0 .8-.4v-7.124a1 1 0 0 1 .247-.659l5.302-6.059c.566-.646.106-1.658-.753-1.658Z"
              />
            </svg>
            Filters
            <svg
              class="w-2.5 h-2.5 ms-2.5"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 10 6"
            >
              <path
                stroke="currentColor"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="m1 1 4 4 4-4"
              />
            </svg>
          </button>
          <template v-if="currentTab === 'donations'">
            <div
              v-show="isDropdownOpen"
              class="absolute z-10 p-4 mt-2 bg-white border border-gray-300 rounded-lg shadow-lg w-80"
            >
              <div class="flex">
                <button
                  type="button"
                  class="ml-auto -mx-1.5 -my-1.5 p-1.5 inline-flex text-gray-500 items-center justify-end h-8 w-8 hover:text-custom-yellow"
                  @click="closeDropdown"
                  aria-label="Close"
                >
                  <span class="sr-only">Dismiss</span>
                  <svg
                    class="w-3 h-3"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 14 14"
                  >
                    <path
                      stroke="currentColor"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
                    />
                  </svg>
                </button>
              </div>
              <form class="max-w-sm mx-auto" @submit.prevent="applyFilters">
                <div class="mb-4">
                  <label
                    for="bloodGroup"
                    class="block mb-2 text-sm font-medium text-gray-900"
                    >Blood Group</label
                  >
                  <select
                    v-model="filters.blood_group"
                    id="bloodGroup"
                    class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5"
                    :disabled="isLoadingBloodGroups || bloodGroups.length === 0"
                  >
                    <option value="" selected>Select blood group</option>
                    <option
                      v-for="group in bloodGroups"
                      :key="group.blood_group_id"
                      :value="group.description"
                    >
                      {{ group.description }}
                    </option>
                  </select>
                  <p
                    v-if="!isLoadingBloodGroups && bloodGroups.length === 0"
                    class="mt-1 text-sm text-red-600"
                  >
                    Failed to load blood groups. Please try again.
                  </p>
                </div>
                <div class="mb-4">
                  <label
                    for="donationTypeFilter"
                    class="block mb-2 text-sm font-medium text-gray-900"
                    >Donation Type</label
                   >
                   <select id="donation_type" v-model="filters.donation_type"
                       class="w-full p-3 bg-gray-100 border rounded shadow-sm appearance-none placeholder-gray shadow-gray-400">
                       <option value="" disabled selected>Select status</option>
                       <option value="Replacement">
                           Replacement (Replaces blood unit for specific patient)
                       </option>
                       <option value="Random">
                           Random (First-time donor, not for specific patient)
                       </option>
                       <option value="Frequent">
                           Regular-donor (Frequent donor, not for specific patient)
                       </option>
                       <option value="Designated">
                           Designated/Directed (Donates for specific patient with doctor's
                           letter)
                       </option>
                       <option value="Autologous">
                           Autologous (Donates for future self use with doctor's letter)
                       </option>
                   </select>
                </div>
                <div class="mb-4">
                  <label
                    for="donationTypeFilter"
                    class="block mb-2 text-sm font-medium text-gray-900"
                    >Donation Process</label
                   >
                   <select id="technology" v-model="filters.technology"
                       class="w-full p-3 text-sm bg-gray-100 border rounded shadow-sm appearance-none placeholder-gray shadow-gray-400">
                       <option value="" disabled>Select Donation Process</option>
                       <option value="Normal">Voluntary/Regular/Normal</option>
                       <option value="Apheresis">Apheresis</option>
                   </select>
                </div>
                <div class="mb-4">
                  <label
                    for="statusFilter"
                    class="block mb-2 text-sm font-medium text-gray-900"
                    >Status</label
                  >
                  <select
                    id="statusFilter"
                    v-model="filters.status"
                    class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5"
                  >
                    <option value="" selected>Select</option>
                    <option value="Unscreened">Unscreened</option>
                    <option value="Approved">Approved</option>
                    <option value="Ongoing Screening">Ongoing Screening</option>
                    <option value="Disposed">Disposed</option>
                  </select>
                </div>
                <div class="mb-4">
                  <label
                    for="dateFrom"
                    class="block mb-2 text-sm font-medium text-gray-900"
                    >Date From</label
                  >
                  <input
                    type="date"
                    v-model="filters.start_date"
                    id="dateFrom"
                    class="w-full px-4 py-2 text-gray-800 border border-gray-300 rounded-sm dark:border-gray-300 dark:placeholder-gray-400"
                    placeholder="Date Range"
                  />
                </div>
                <div class="mb-4">
                  <label
                    for="dateTo"
                    class="block mb-2 text-sm font-medium text-gray-900"
                    >Date To</label
                  >
                  <input
                    type="date"
                    v-model="filters.end_date"
                    id="dateTo"
                    class="w-full px-4 py-2 text-gray-800 border border-gray-300 rounded-sm dark:border-gray-300 dark:placeholder-gray-400"
                    placeholder="Date Range"
                  />
                </div>
                <div class="flex justify-end space-x-2">
                  <button
                    type="button"
                    @click="cancelFilters"
                    class="px-4 py-2 text-sm font-medium text-gray-900 bg-white border border-gray-300 rounded-lg hover:bg-gray-100"
                  >
                    Clear
                  </button>
                  <button
                    type="submit"
                    class="px-4 py-2 text-sm font-medium text-white rounded-lg bg-custom-yellow hover:bg-custom-yellow-dark"
                  >
                    Filter
                  </button>
                </div>
              </form>
            </div>
          </template>
          <template v-if="currentTab === 'reactions'">
            <div
              v-show="isDropdownOpen"
              class="absolute z-10 p-4 mt-2 bg-white border border-gray-300 rounded-lg shadow-lg w-80"
            >
              <div class="flex">
                <button
                  type="button"
                  class="ml-auto -mx-1.5 -my-1.5 p-1.5 inline-flex text-gray-500 items-center justify-end h-8 w-8 hover:text-custom-yellow"
                  @click="cancelFilters"
                  aria-label="Close"
                >
                  <span class="sr-only">Dismiss</span>
                  <svg
                    class="w-3 h-3"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 14 14"
                  >
                    <path
                      stroke="currentColor"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
                    />
                  </svg>
                </button>
              </div>
              <form class="max-w-sm mx-auto" @submit.prevent="applyFilters">
                <div class="mb-4">
                  <label
                    for="dateFrom"
                    class="block mb-2 text-sm font-medium text-gray-900"
                    >Date From</label
                  >
                  <input
                    type="date"
                    v-model="filters.start_date"
                    id="dateFrom"
                    class="w-full px-4 py-2 text-gray-800 border border-gray-300 rounded-sm dark:border-gray-300 dark:placeholder-gray-400"
                    placeholder="Date Range"
                  />
                </div>
                <div class="mb-4">
                  <label
                    for="dateTo"
                    class="block mb-2 text-sm font-medium text-gray-900"
                    >Date To</label
                  >
                  <input
                    type="date"
                    v-model="filters.end_date"
                    id="dateTo"
                    class="w-full px-4 py-2 text-gray-800 border border-gray-300 rounded-sm dark:border-gray-300 dark:placeholder-gray-400"
                    placeholder="Date Range"
                  />
                </div>
                <div class="flex justify-end space-x-2">
                  <button
                    type="button"
                    @click="cancelFilters"
                    class="px-4 py-2 text-sm font-medium text-gray-900 bg-white border border-gray-300 rounded-lg hover:bg-gray-100"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    class="px-4 py-2 text-sm font-medium text-white rounded-lg bg-custom-yellow hover:bg-custom-yellow-dark"
                  >
                    Filter
                  </button>
                </div>
              </form>
            </div>
          </template>
        </div>
        <!-- Search Input -->
        <div class="relative">
          <div class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <svg
              class="w-5 h-5 text-gray-500"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 20 20"
            >
              <path
                stroke="currentColor"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z"
              />
            </svg>
          </div>
          <input
            v-model="searchQuery"
            type="text"
            id="searchInput"
            class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full pl-10 p-2.5"
            placeholder="Search..."
            @input="applyFiltersToCurrentTab"
          />
        </div>
      </div>
      <div class="flex items-center mt-4 space-x-4 sm:mt-0">
        <div class="relative flex">
          <template v-if="currentTab === 'donations'">
            <CustomButton
              label="Download"
              bg-class="bg-custom-yellow"
              @click="handleDownloadDonationReport"
            />
          </template>
          <template v-if="currentTab === 'unit-tally'">
            <CustomButton
              label="Download"
              bg-class="bg-custom-yellow"
              @click="handleDownloadTallyReport"
            />
          </template>
          <template v-if="currentTab === 'reactions'">
            <CustomButton
              label="Download"
              bg-class="bg-custom-yellow"
              @click="handleDownloadReactionReport"
            />
          </template>
          <template v-if="currentTab === 'ratios'">
            <CustomButton
              label="Download"
              bg-class="bg-custom-yellow"
              @click="handleDownloadRatiosReport"
            />
          </template>
        </div>
      </div>
      <div
        v-if="isPageLoading"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      >
        <div
          class="w-12 h-12 border-t-4 border-b-4 border-yellow-500 rounded-full animate-spin"
        ></div>
      </div>
    </div>
    <div class="flex mb-4 border-b tabs">
      <button
        class="px-4 py-2 -mb-px font-semibold text-gray-500 border-b-2"
        :class="{ 'border-custom-yellow': currentTab === 'donations' }"
        @click="currentTab = 'donations'"
      >
        Dashboard
      </button>
      <button
        class="px-4 py-2 -mb-px font-semibold text-gray-500 border-b-2"
        :class="{ 'border-custom-yellow': currentTab === 'unit-tally' }"
        @click="currentTab = 'unit-tally'"
      >
        Units Tally
      </button>
      <button
        class="px-4 py-2 -mb-px font-semibold text-gray-500 border-b-2"
        :class="{ 'border-custom-yellow': currentTab === 'reactions' }"
        @click="currentTab = 'reactions'"
      >
        Reaction
      </button>
    </div>
    <component
      :is="currentTabComponent"
      ref="currentTabComponent"
      :search-query="searchQuery"
      :filters="filters"
    ></component>
  </div>
</template>

<script>
import AlertLayout from "@/components/AlertLayout.vue";
import CustomButton from "@/components/CustomButton.vue";
import BloodDonationsReportTab from "../tabs/BloodDonationsReportTab.vue";
import DonorReactionsReportsTab from "../tabs/DonorReactionsReportsTab.vue";
import TallyLogReportTab from "../tabs/TallyLogReportTab.vue";
import api from "@/services/api";

export default {
  name: "TallyReportsPage",
  components: {
    AlertLayout,
    CustomButton,
    BloodDonationsReportTab,
    TallyLogReportTab,
    DonorReactionsReportsTab,
  },
  data() {
    return {
      searchQuery: "",
      isDropdownOpen: false,
      currentTab: "donations",
      user: [],
      isPageLoading: false,
      isLoadingBloodGroups: false,
      bloodGroups: [],
      filters: {
        blood_group: "",
        status: "",
        start_date: "",
        end_date: "",
        donation_type: "",
        technology: ""
      },
    };
  },
  created() {
    // Restore the currentTab from localStorage if it exists
    const savedTab = localStorage.getItem('tallyReportsPageTab');
    if (savedTab && [
      'blood-units',
      'issued-units',
      'external-issuance'
    ].includes(savedTab)) {
      this.currentTab = savedTab;
    }
  },
  watch: {
    // Watch for changes to currentTab and save to localStorage
    currentTab(newTab) {
      localStorage.setItem('tallyReportsPageTab', newTab);
    }
  },
  computed: {
    currentTabComponent() {
      switch (this.currentTab) {
        case "donations":
          return "BloodDonationsReportTab";
        case "unit-tally":
          return "TallyLogReportTab";
        case "reactions":
          return "DonorReactionsReportsTab";
        default:
          return "BloodDonationsReportTab";
      }
    },
  },
  methods: {
    async fetchBloodGroups() {
      this.isLoadingBloodGroups = true;
      try {
        const response = await api.getBloodGroups();
        this.bloodGroups = response.data || response || [];
        console.log("Blood Groups Loaded:", this.bloodGroups); // Debug log
      } catch (error) {
        console.error("Error fetching blood groups:", error);
        this.bloodGroups = [];
      } finally {
        this.isLoadingBloodGroups = false;
      }
    },
    toggleDropdown() {
      this.isDropdownOpen = !this.isDropdownOpen;
    },
    hasManageClaim(value) {
      const claims = this.user?.user_roles?.[0]?.role?.claims || [];
      return claims.some((claim) => claim.name === value);
    },
    handleDownloadDonationReport() {
      if (this.$refs.currentTabComponent?.handleDownloadDonationReport) {
        this.$refs.currentTabComponent.handleDownloadDonationReport();
      }
    },
    handleDownloadTallyReport() {
      if (this.$refs.currentTabComponent?.handleDownloadTallyReport) {
        this.$refs.currentTabComponent.handleDownloadTallyReport();
      }
    },
    handleDownloadReactionReport() {
      if (this.$refs.currentTabComponent?.handleDownloadReactionReport) {
        this.$refs.currentTabComponent.handleDownloadReactionReport();
      }
    },
    handleDownloadRatiosReport() {
      if (this.$refs.currentTabComponent?.handleDownloadRatiosReport) {
        this.$refs.currentTabComponent.handleDownloadRatiosReport();
      }
    },
    applyFilters() {
      this.applyFiltersToCurrentTab();
      this.isDropdownOpen = false;
    },
    applyFiltersToCurrentTab() {
      if (
        this.$refs.currentTabComponent &&
        typeof this.$refs.currentTabComponent.applyFilters === "function"
      ) {
        this.$refs.currentTabComponent.applyFilters(this.filters, this.searchQuery);
      } else {
        console.error("applyFilters method not found on current tab component");
      }
    },
    closeDropdown() {
      this.isDropdownOpen = false;
    },
    cancelFilters() {
      this.filters = {
        blood_group: "",
        status: "",
        start_date: "",
        end_date: "",
        donation_type: "",
        technology: ""
      };
      this.searchQuery = "";
      this.applyFiltersToCurrentTab();
      this.isDropdownOpen = false;
    },
  },
  mounted() {
    this.user = this.$store.getters["auth/user"];
    this.fetchBloodGroups();
  },
};
</script>

<style lang="css" scoped>
input:focus,
select:focus {
  outline: none;
  border-color: #facc15;
  box-shadow: 0 0 0 2px rgba(250, 204, 21, 0.2);
}
</style>